import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { removeFcmTokenOnLogout } from '@/utils/pushUtils';
import { Spinner } from './spinner/spinner';
import { isAuthenticated } from './services/credentialsService';
import { useTranslation } from 'react-i18next';
import { api } from '@/utils/api';
import { isWorkerProfileComplete } from '@/utils/workerProfileUtils';
import { WorkerProfile } from '@/types/userTypes';
interface AvatarDropdownProps {
  user: any;
  onLogout: () => void;
}

const AvatarDropdown: React.FC<AvatarDropdownProps> = ({ user, onLogout }) => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState<boolean>(true);
  const [workerProfileData, setWorkerProfileData] = useState<WorkerProfile>();

  // Check worker profile completeness (same logic as sidebar)
  useEffect(() => {
    const fetchWorkerProfile = async () => {
      if (user?.type === 'worker') {
        try {
          const workerProfile = await api.getMyWorkerProfile();
          if (workerProfile) {
            setWorkerProfileData(workerProfile);
            // Check if profile is complete
            const complete = isWorkerProfileComplete(workerProfile);
            setIsProfileComplete(complete);
          }
        } catch (err) {
          console.error('Error fetching worker profile:', err);
          // Default to true if fetch fails to avoid blocking
          setIsProfileComplete(true);
        }
      } else {
        // For non-workers, always set to true (no restrictions)
        setIsProfileComplete(true);
      }
    };

    fetchWorkerProfile();
  }, [user?.type]);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleDashboard = () => {
    // Prevent navigation if worker profile is incomplete
    if (user?.type === 'worker' && !isProfileComplete) {
      return;
    }
    
    handleMenuClose();
    if (user?.type === 'worker') {
      navigate('/my-jobs');
    } else if (user?.type === 'user') {
      // Client dashboard - navigate to my proposals
      navigate('/my-proposals');
    } else {
      // Fallback to home if user type is unknown
      navigate('/');
    }
  };
  const handleSettings = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handleMessages = () => {
    // Prevent navigation if worker profile is incomplete
    if (user?.type === 'worker' && !isProfileComplete) {
      return;
    }
    
    handleMenuClose();
    navigate('/chat');
  };

  const handleLogoutClick = async () => {
    setIsLoading(true);
    await removeFcmTokenOnLogout()
    onLogout();
    handleMenuClose();
    setIsLoading(false);
  };

  const handleProfileIconClick = (e: React.MouseEvent) => {
    // If clicking directly on profile icon, navigate to dashboard (only if profile is complete)
    // Otherwise, open the menu
    if ((e.target as HTMLElement).closest('.MuiAvatar-root')) {
      // Only navigate if profile is complete (for workers)
      if (user?.type === 'worker' && !isProfileComplete) {
        // If incomplete, just open menu instead
        handleMenuOpen(e);
      } else {
        handleDashboard();
      }
    } else {
      handleMenuOpen(e);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      <Box
        onClick={handleProfileIconClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'white',
          borderRadius: 3,
          px: 2,
          py: 1,
          boxShadow: 2,
          cursor: 'pointer',
          gap: 1.5,
          flexWrap: 'nowrap',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 500, color: '#222', width:90 }}>
          {t('myProfile')}
        </Typography>
        <Avatar
          src={user?.avatar || ''}
          alt={user?.name || user?.email || 'Usuario'}
          sx={{ width: 45, height: 45 }}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 180,
            borderRadius: 2,
            boxShadow: 3,
            p: 0.5,
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {isAuthenticated() && (
          <MenuItem 
            onClick={handleDashboard} 
            disabled={user?.type === 'worker' && !isProfileComplete}
            sx={{ 
              borderRadius: 1, 
              mb: 0.5, 
              fontWeight: 500, 
              bgcolor: 'rgba(66,133,244,0.07)',
              ...(user?.type === 'worker' && !isProfileComplete && {
                opacity: 0.5,
                cursor: 'not-allowed',
              })
            }}
          >
            {t('Panel')}
          </MenuItem>
        )}
      {isAuthenticated() && (
  <MenuItem 
    onClick={handleMessages} 
    disabled={user?.type === 'worker' && !isProfileComplete}
    sx={{ 
      borderRadius: 1, 
      mb: 0.5, 
      position: 'relative',
      ...(user?.type === 'worker' && !isProfileComplete && {
        opacity: 0.5,
        cursor: 'not-allowed',
      })
    }}
  >
   {user?.unSeenMessageCounter > 0 && (
  <span
    style={{
      position: 'absolute',
      top: '2px',
      right: '8px',  // move from left to right
      backgroundColor: 'red',
      color: 'white',
      borderRadius: '50%',
      padding: '2px 6px',
      fontSize: '10px',
      fontWeight: 'bold',
      lineHeight: 1,
      minWidth: '16px',
      textAlign: 'center',
      background:"red"
    }}
  >
    {user?.unSeenMessageCounter}
  </span>
)}
<span >
  {t('messages')}
</span>
  </MenuItem>
)}

        {isAuthenticated() && <MenuItem onClick={handleSettings} sx={{ borderRadius: 1, mb: 0.5 }}>
          {t('settings')}
        </MenuItem>}
        <MenuItem disabled={isLoading} onClick={handleLogoutClick} sx={{ borderRadius: 1 }}>
        <div className='flex items-center gap-2'>
         <Spinner size={'xsmall'} isLoading={isLoading} /> {t('logout')}
         </div>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AvatarDropdown; 